import {
  Component,
  EventEmitter,
  Output,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription, firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NotesService, Note } from '../services/notes.service';
import { MatDialog } from '@angular/material/dialog';
import { NoteDetailDialogComponent } from '../authentication/dialogs/note-detail-dialog/note-detail-dialog.component';
import { ShareNoteDialogComponent, ShareNoteDialogResult } from '../authentication/dialogs/share-note-dialog/share-note-dialog.component';

import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';

type Hsl = { h: number; s: number; l: number };
type NoteTemplate = {
  title: string;
  content: string;
  labels?: string;
  color?: string;
  pinned?: boolean;
};

const DEFAULT_NOTE_COLOR = 'transparent';

@Component({
  selector: 'app-clari-notes',
  standalone: true,
  templateUrl: './clari-notes.component.html',
  styleUrls: ['./clari-notes.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DragDropModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatSnackBarModule
  ]
})
export class ClariNotesComponent implements OnInit, OnDestroy {

  @Output() insertIntoChat = new EventEmitter<string>();

  noteForm: FormGroup;
  searchForm: FormGroup;

  /** All notes from service */
  private allNotes: Note[] = [];

  /** View-model lists */
  pinnedNotes: Note[] = [];
  otherNotes: Note[] = [];

  labels: string[] = [];
  selectedLabel: string | null = null;
  showArchived = false;

  /* ------------------------------------------------------------------ */
  /*  Select button (below)                                             */
  /* ------------------------------------------------------------------ */
  selectionMode = false;
  selectedNoteIds = new Set<string>();

  get selectedCount(): number {
    return this.selectedNoteIds.size;
  }

  toggleSelectionMode() {
    this.selectionMode = !this.selectionMode;

    // if canceling selection mode, clear selected
    if (!this.selectionMode) {
      this.selectedNoteIds.clear();
    }
  }

  onNoteClick(n: any, ev: MouseEvent) {
    // selection mode → toggle select instead of opening
    if (this.selectionMode) {
      this.toggleNoteSelection(n, ev);
      return;
    }

    // normal mode → open dialog
    this.openNoteDialog(n);
  }

  toggleNoteSelection(n: any, ev?: MouseEvent) {
    ev?.stopPropagation(); // prevent opening dialog

    if (!n?.id) return;

    if (this.selectedNoteIds.has(n.id)) {
      this.selectedNoteIds.delete(n.id);
    } else {
      this.selectedNoteIds.add(n.id);
    }
  }

  isSelected(n: any): boolean {
    return !!n?.id && this.selectedNoteIds.has(n.id);
  }

  async deleteSelected(): Promise<void> {
    const ids = Array.from(this.selectedNoteIds);
    if (!ids.length) return;

    // OPTIONAL CONFIRM
    const confirmDelete = confirm(`Delete ${ids.length} notes?`);
    if (!confirmDelete) return;

    // 🔥 delete all notes
    await Promise.all(ids.map(id => this.notesService.deleteNote(id)));

    // Clear selection after delete
    this.selectedNoteIds.clear();
    this.selectionMode = false;
  }
  /* ------------------------------------------------------------------ */
  /*  Select button (above)                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Kept as quick-pick presets (still handy),
   * but primary selection is now spectrum + native picker.
   */
  colorPalette = [
    DEFAULT_NOTE_COLOR,
    '#fff8e1',
    '#e3f2fd',
    '#fce4ec',
    '#e8f5e9',
    '#ede7f6',
    '#f3e5f5',
    '#ffebee',
    '#e0f7fa'
  ];

  /** Quick templates to seed new notes */
  noteTemplates: NoteTemplate[] = [
    {
      title: 'Video concept',
      content: 'Draft a short concept for an upcoming video.',
      labels: 'video,idea',
      color: '#fff8e1',
      pinned: false
    },
    {
      title: 'Client recap',
      content: 'Summarize key points and next steps.',
      labels: 'client,follow-up',
      color: '#e3f2fd',
      pinned: false
    }
  ];

  /** New note spectrum state */
  newNoteHsl: Hsl = { h: 0, s: 73, l: 20 };

  /**
   * Existing notes spectrum state (per note id),
   * used inside the color menu with realtime preview + debounced DB writes.
   */
  private noteHslById = new Map<string, Hsl>();
  private colorWriteTimers = new Map<string, any>();

  private sub = new Subscription();

  constructor(
    private fb: FormBuilder,
    private notesService: NotesService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.noteForm = this.fb.group({
      title: [''],
      content: [''],
      labels: [''],
      color: [DEFAULT_NOTE_COLOR],
      pinned: [false]
    });

    this.searchForm = this.fb.group({
      query: ['']
    });

    // init spectrum from default form color
    this.syncNewNoteSpectrumFromHex(this.noteForm.value.color || DEFAULT_NOTE_COLOR);

    // keep spectrum in sync if color changes through any method
    this.sub.add(
      this.noteForm.get('color')!.valueChanges.subscribe((hex: string) => {
        if (typeof hex === 'string' && hex.startsWith('#')) {
          this.syncNewNoteSpectrumFromHex(hex);
        }
      })
    );
  }

  ngOnInit(): void {
    this.sub.add(
      this.notesService.notes$.subscribe(notes => {
        this.allNotes = notes;
        this.refreshViewFromNotes();
      })
    );

    this.sub.add(
      this.searchForm.get('query')!.valueChanges.subscribe(() => {
        this.refreshViewFromNotes();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    // clear any pending timers
    for (const t of this.colorWriteTimers.values()) {
      try { clearTimeout(t); } catch {}
    }
    this.colorWriteTimers.clear();
  }

  /* ------------------------------------------------------------------ */
  /*  Creating a note (below)                                           */
  /* ------------------------------------------------------------------ */
  async createNote(): Promise<void> {
    const { title, content, labels, color, pinned } = this.noteForm.value;

    if (!title.trim() && !content.trim()) {
      return;
    }

    const labelList = (labels || '')
      .split(',')
      .map((l: string) => l.trim())
      .filter((l: string) => l.length);

    try {
      await this.notesService.createNote({
        title,
        content,
        labels: labelList,
        color,
        pinned
      });

      // ✅ reset only if save succeeds
      this.noteForm.reset({
        title: '',
        content: '',
        labels: '',
        color: DEFAULT_NOTE_COLOR,
        pinned: false
      });

      // reset spectrum too
      this.syncNewNoteSpectrumFromHex(DEFAULT_NOTE_COLOR);

    } catch (err: any) {
      const msgText = (err?.message || err?.toString() || '').toLowerCase();

      if (msgText.includes('user not initialized')) {
        this.showActionToast('Please log in to save notes', 'warn');
      } else {
        this.showActionToast('Failed to save note', 'warn');
      }

      console.error('createNote error:', err);
    }
  }

  applyNoteTemplate(template: NoteTemplate): void {
    this.noteForm.patchValue({
      title: template.title || '',
      content: template.content || '',
      labels: template.labels || '',
      color: template.color || DEFAULT_NOTE_COLOR,
      pinned: template.pinned ?? false
    });
  }

  private showActionToast(message: string, type: 'warn' | 'success' | 'info' = 'info') {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [`toast-${type}`]
    });
  }
  /* ------------------------------------------------------------------ */
  /*  Creating a note (above)                                           */
  /* ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ */
  /*  UI helpers                                                        */
  /* ------------------------------------------------------------------ */

  get filteredPinned(): Note[] {
    return this.applyFilters(this.pinnedNotes);
  }

  get filteredOthers(): Note[] {
    return this.applyFilters(this.otherNotes);
  }

  private applyFilters(list: Note[]): Note[] {
    let res = list;

    if (!this.showArchived) {
      res = res.filter(n => !n.archived);
    }

    if (this.selectedLabel) {
      res = res.filter(n => n.labels?.includes(this.selectedLabel!));
    }

    const q: string = this.searchForm.get('query')!.value || '';
    if (q.trim()) {
      const needle = q.toLowerCase();
      res = res.filter(n =>
        (n.title || '').toLowerCase().includes(needle) ||
        (n.content || '').toLowerCase().includes(needle) ||
        (n.labels || []).some(l => l.toLowerCase().includes(needle))
      );
    }

    return res;
  }

  private refreshViewFromNotes(): void {
    this.pinnedNotes = this.allNotes.filter(n => n.pinned);
    this.otherNotes  = this.allNotes.filter(n => !n.pinned);

    // labels (unique, sorted)
    const labelSet = new Set<string>();
    this.allNotes.forEach(n => (n.labels || []).forEach(l => labelSet.add(l)));
    this.labels = Array.from(labelSet).sort();
  }

  selectLabel(label: string | null): void {
    this.selectedLabel = label;
  }

  toggleArchivedView(): void {
    this.showArchived = !this.showArchived;
  }

  async togglePinned(note: Note, pinned: boolean): Promise<void> {
    await this.notesService.togglePinned(note.id, pinned);
  }

  async archive(note: Note): Promise<void> {
    await this.notesService.toggleArchived(note.id, !note.archived);
  }

  async delete(note: Note): Promise<void> {
    if (confirm('Delete this note permanently?')) {
      await this.notesService.deleteNote(note.id);
    }
  }

  async changeColor(note: Note, color: string): Promise<void> {
    await this.notesService.updateNote(note.id, { color });
  }

  useInChat(note: Note): void {
    const text = `${note.title ? note.title + '\n\n' : ''}${note.content}`;
    this.insertIntoChat.emit(text);
  }

  async shareNote(note: Note, event?: MouseEvent): Promise<void> {
    event?.stopPropagation();

    const dialogRef = this.dialog.open<ShareNoteDialogComponent, { title: string }, ShareNoteDialogResult>(
      ShareNoteDialogComponent,
      {
        width: '420px',
        data: { title: note.title || 'Untitled' }
      }
    );

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result) return;

    try {
      await this.notesService.shareNoteWithUser(note, result.email, result.message);
      this.showActionToast('Note shared successfully', 'success');
    } catch (err) {
      console.error('shareNote error:', err);
      this.showActionToast('Unable to share note', 'warn');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Drag & drop                                                       */
  /* ------------------------------------------------------------------ */

  dropPinned(ev: CdkDragDrop<Note[]>): void {
    if (ev.previousContainer === ev.container) {
      moveItemInArray(this.pinnedNotes, ev.previousIndex, ev.currentIndex);
    } else {
      transferArrayItem(
        ev.previousContainer.data,
        ev.container.data,
        ev.previousIndex,
        ev.currentIndex
      );
    }
    this.persistOrder();
  }

  dropOthers(ev: CdkDragDrop<Note[]>): void {
    if (ev.previousContainer === ev.container) {
      moveItemInArray(this.otherNotes, ev.previousIndex, ev.currentIndex);
    } else {
      transferArrayItem(
        ev.previousContainer.data,
        ev.container.data,
        ev.previousIndex,
        ev.currentIndex
      );
    }
    this.persistOrder();
  }

  private async persistOrder(): Promise<void> {
    const orderedIds = [
      ...this.pinnedNotes.map(n => n.id),
      ...this.otherNotes.map(n => n.id)
    ];
    await this.notesService.reorderNotes(orderedIds);
  }

  isLong(note: Note): boolean {
    const contentLength = (note.content || '').length;
    // Tweak this threshold to taste
    return contentLength > 260;
  }

  openNoteDialog(note: Note, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation(); // avoid interfering with drag
    }

    this.dialog.open(NoteDetailDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '80vh',
      data: { note }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  ✅ NEW: Spectrum + Color Picker logic                              */
  /* ------------------------------------------------------------------ */

  /** Quick preset click for new note */
  setNewNoteColor(hex: string): void {
    const cleaned = this.normalizeHex(hex);
    this.noteForm.patchValue({ color: cleaned });
    this.syncNewNoteSpectrumFromHex(cleaned);
  }

  /** Native <input type="color"> for new note */
  onNewNoteHexInput(ev: Event): void {
    const hex = (ev.target as HTMLInputElement)?.value || DEFAULT_NOTE_COLOR;
    this.setNewNoteColor(hex);
  }

  onNewNoteHueInput(ev: Event): void {
    const h = Number((ev.target as HTMLInputElement).value);
    this.newNoteHsl.h = this.clamp(h, 0, 360);
    this.applyNewNoteHslToForm();
  }

  onNewNoteLightnessInput(ev: Event): void {
    const l = Number((ev.target as HTMLInputElement).value);
    this.newNoteHsl.l = this.clamp(l, 20, 95);
    this.applyNewNoteHslToForm();
  }

  private applyNewNoteHslToForm(): void {
    const hex = this.hslToHex(this.newNoteHsl.h, this.newNoteHsl.s, this.newNoteHsl.l);
    this.noteForm.patchValue({ color: hex }, { emitEvent: true });
  }

  private syncNewNoteSpectrumFromHex(hex: string): void {
    const hsl = this.hexToHsl(hex);
    if (hsl) {
      // keep your chosen saturation (nice UI feel) but learn hue/lightness from hex
      this.newNoteHsl = { h: hsl.h, s: this.newNoteHsl.s, l: hsl.l };
    }
  }

  /** Existing note: quick preset */
  setExistingNoteColor(note: Note, hex: string, ev?: MouseEvent): void {
    ev?.stopPropagation();
    const cleaned = this.normalizeHex(hex);
    const hsl = this.hexToHsl(cleaned) ?? { h: 210, s: 65, l: 92 };
    this.noteHslById.set(note.id, { ...hsl, s: 65 }); // keep stable saturation for consistent spectrum feel
    this.applyExistingColorLive(note, cleaned);
  }

  /** Existing note: native picker input */
  onExistingHexInput(note: Note, ev: Event): void {
    ev.stopPropagation();
    const hex = (ev.target as HTMLInputElement)?.value || note.color || '#ffffff';
    this.setExistingNoteColor(note, hex);
  }

  /** Existing note: hue slider input */
  onExistingHueInput(note: Note, ev: Event): void {
    ev.stopPropagation();
    const h = Number((ev.target as HTMLInputElement).value);
    const hsl = this.ensureNoteHsl(note);
    hsl.h = this.clamp(h, 0, 360);
    this.noteHslById.set(note.id, hsl);

    const hex = this.hslToHex(hsl.h, hsl.s, hsl.l);
    this.applyExistingColorLive(note, hex);
  }

  /** Existing note: lightness slider input */
  onExistingLightnessInput(note: Note, ev: Event): void {
    ev.stopPropagation();
    const l = Number((ev.target as HTMLInputElement).value);
    const hsl = this.ensureNoteHsl(note);
    hsl.l = this.clamp(l, 20, 95);
    this.noteHslById.set(note.id, hsl);

    const hex = this.hslToHex(hsl.h, hsl.s, hsl.l);
    this.applyExistingColorLive(note, hex);
  }

  getExistingHue(note: Note): number {
    return this.ensureNoteHsl(note).h;
  }

  getExistingLightness(note: Note): number {
    return this.ensureNoteHsl(note).l;
  }

  private ensureNoteHsl(note: Note): Hsl {
    const cached = this.noteHslById.get(note.id);
    if (cached) return cached;

    const parsed = this.hexToHsl(note.color || '#ffffff') ?? { h: 210, s: 65, l: 92 };
    // we keep saturation stable for UI; still preserve hue/lightness
    const hsl: Hsl = { h: parsed.h, s: 65, l: parsed.l };
    this.noteHslById.set(note.id, hsl);
    return hsl;
  }

  /**
   * Realtime UI update + debounced Firebase write.
   * - updates note object immediately (so UI changes instantly)
   * - schedules a DB update after a short delay (prevents write spam)
   */
  private applyExistingColorLive(note: Note, hex: string): void {
    const cleaned = this.normalizeHex(hex);
    note.color = cleaned; // optimistic UI
    this.scheduleColorPersist(note.id, cleaned);
  }

  private scheduleColorPersist(noteId: string, hex: string): void {
    const prev = this.colorWriteTimers.get(noteId);
    if (prev) {
      try { clearTimeout(prev); } catch {}
    }

    const t = setTimeout(async () => {
      try {
        await this.notesService.updateNote(noteId, { color: hex });
      } catch (e) {
        console.error('Failed to persist color', e);
        this.showActionToast('Failed to update color', 'warn');
      }
    }, 250);

    this.colorWriteTimers.set(noteId, t);
  }

  /** Foreground color helper so text stays readable on dark colors */
  getReadableTextColor(bgHex: string): string {
    if (!bgHex || String(bgHex).toLowerCase().trim() === 'transparent') {
      return '#f8fafc';
    }
    const rgb = this.hexToRgb(bgHex);
    if (!rgb) return '#111827';

    // relative luminance (WCAG-ish)
    const [r, g, b] = rgb.map(v => v / 255).map(c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    // tweak threshold to taste
    return lum > 0.55 ? '#0f172a' : '#f8fafc';
  }

  /* ------------------------------------------------------------------ */
  /*  Color math helpers                                                */
  /* ------------------------------------------------------------------ */

  private normalizeHex(hex: string): string {
    if (!hex) return '#ffffff';
    const h = hex.trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(h)) return h;
    // try to expand #rgb -> #rrggbb
    if (/^#[0-9a-f]{3}$/.test(h)) {
      const r = h[1], g = h[2], b = h[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    return '#ffffff';
  }

  private clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, Number.isFinite(n) ? n : min));
  }

  private hexToRgb(hex: string): [number, number, number] | null {
    const h = this.normalizeHex(hex).replace('#', '');
    if (h.length !== 6) return null;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if ([r, g, b].some(n => Number.isNaN(n))) return null;
    return [r, g, b];
  }

  private rgbToHsl(r: number, g: number, b: number): Hsl {
    r /= 255; g /= 255; b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (d !== 0) {
      s = d / (1 - Math.abs(2 * l - 1));
      switch (max) {
        case r: h = 60 * (((g - b) / d) % 6); break;
        case g: h = 60 * (((b - r) / d) + 2); break;
        case b: h = 60 * (((r - g) / d) + 4); break;
      }
    }

    if (h < 0) h += 360;

    return {
      h: Math.round(h),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  }

  private hexToHsl(hex: string): Hsl | null {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return null;
    return this.rgbToHsl(rgb[0], rgb[1], rgb[2]);
  }

  private hslToHex(h: number, s: number, l: number): string {
    h = this.clamp(h, 0, 360);
    s = this.clamp(s, 0, 100);
    l = this.clamp(l, 0, 100);

    const _s = s / 100;
    const _l = l / 100;

    const c = (1 - Math.abs(2 * _l - 1)) * _s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = _l - c / 2;

    let r = 0, g = 0, b = 0;

    if (h >= 0 && h < 60)      { r = c; g = x; b = 0; }
    else if (h < 120)         { r = x; g = c; b = 0; }
    else if (h < 180)         { r = 0; g = c; b = x; }
    else if (h < 240)         { r = 0; g = x; b = c; }
    else if (h < 300)         { r = x; g = 0; b = c; }
    else                      { r = c; g = 0; b = x; }

    const R = Math.round((r + m) * 255);
    const G = Math.round((g + m) * 255);
    const B = Math.round((b + m) * 255);

    return this.rgbToHex(R, G, B);
  }

  private rgbToHex(r: number, g: number, b: number): string {
    const to2 = (n: number) => n.toString(16).padStart(2, '0');
    return `#${to2(this.clamp(Math.round(r), 0, 255))}${to2(this.clamp(Math.round(g), 0, 255))}${to2(this.clamp(Math.round(b), 0, 255))}`;
  }
}
